import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AllianceSummary } from '@kaeliux/shared';
import { cn } from '../utils/cn';
import { formatFullNumber } from '../utils/formatters';
import { Table, Th, Td, TABLE_ROW_HIGHLIGHT_CLASSES, TABLE_ROW_BORDER_CLASSES } from './ui/Table';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { Input } from './ui/Input';
import AllianceLeaderIcon from './AllianceLeaderIcon';

interface AllianceBrowseListProps {
    alliances: AllianceSummary[];
    /** Non-null when the viewer is already in an alliance — apply actions are hidden. */
    myAllianceId: string | null;
    pendingApplicationIds: string[];
    search: string;
    onSearchChange: (value: string) => void;
    /** Passes the whole row so the caller can name the alliance in its apply dialog. */
    onApply: (alliance: AllianceSummary) => void;
    onCancelApplication: (allianceId: string) => void;
    onOpen: (allianceId: string) => void;
    isPending: boolean;
    busyAllianceId: string | null;
}

/**
 * Directory of every alliance, ordered by aggregated total points
 * descending — the server returns them pre-sorted, which makes this list the
 * alliance leaderboard too (`#1` is the top-scoring alliance, not just the
 * first row).
 */
export default function AllianceBrowseList({
    alliances,
    myAllianceId,
    pendingApplicationIds,
    search,
    onSearchChange,
    onApply,
    onCancelApplication,
    onOpen,
    isPending,
    busyAllianceId,
}: AllianceBrowseListProps) {
    // Local mirror so typing stays responsive while the debounced/parent
    // query catches up. Re-synced when `search` changes from the outside, so
    // the box cannot keep showing a stale term if the parent ever resets or
    // restores one (today the tab unmounts on every switch, which hides the
    // divergence — this stops it becoming a bug the day it doesn't).
    const [draft, setDraft] = useState(search);
    useEffect(() => setDraft(search), [search]);

    return (
        <div className="flex flex-col gap-3" data-testid="alliance-browse">
            <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                    e.preventDefault();
                    onSearchChange(draft.trim());
                }}
            >
                <Input
                    className="flex-1"
                    value={draft}
                    placeholder="Search by name or tag"
                    aria-label="Search alliances"
                    onChange={(e) => setDraft(e.target.value)}
                />
                <Button type="submit" variant="secondary" size="sm">Search</Button>
            </form>

            {isPending ? (
                <p className="text-content-muted animate-pulse">Loading alliances…</p>
            ) : alliances.length === 0 ? (
                <p className="text-content-muted" data-testid="alliance-browse-empty">No alliances found.</p>
            ) : (
                <Table stickyHeader>
                    <thead>
                        <tr>
                            <Th sticky>#</Th>
                            <Th sticky>Alliance</Th>
                            <Th sticky>Members</Th>
                            <Th sticky>Suns</Th>
                            <Th sticky>Alliance Points</Th>
                            <Th sticky>Leader</Th>
                            <Th sticky>Recruiting</Th>
                            <Th sticky srOnly>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                            {alliances.map((alliance, index) => {
                                const isMine = alliance.id === myAllianceId;
                                const hasApplied = pendingApplicationIds.includes(alliance.id);
                                const busy = busyAllianceId === alliance.id;
                                return (
                                    <tr
                                        key={alliance.id}
                                        className={cn(
                                            TABLE_ROW_BORDER_CLASSES,
                                            isMine && TABLE_ROW_HIGHLIGHT_CLASSES,
                                        )}
                                        data-testid={`alliance-row-${alliance.tag}`}
                                    >
                                        <Td className="text-content-secondary">#{index + 1}</Td>
                                        <Td>
                                            <button
                                                type="button"
                                                className="text-content-primary hover:underline text-left"
                                                onClick={() => onOpen(alliance.id)}
                                            >
                                                <span className="text-content-muted">[{alliance.tag}]</span> {alliance.name}
                                            </button>
                                        </Td>
                                        <Td className="text-content-muted">{alliance.memberCount}</Td>
                                        <Td className="text-content-muted" data-testid="alliance-row-suns">
                                            {alliance.sunCountIsMinimum ? `${alliance.sunCount} or more` : alliance.sunCount}
                                        </Td>
                                        <Td className="text-content-primary">
                                            {formatFullNumber(alliance.totalPoints)}
                                        </Td>
                                        <Td className="text-content-muted">
                                            {alliance.leaderName ? (
                                                <Link
                                                    to={`/player/${alliance.leaderId}`}
                                                    className="inline-flex items-center gap-1 text-content-secondary hover:text-content-primary hover:underline"
                                                >
                                                    <AllianceLeaderIcon />
                                                    <span>{alliance.leaderName}</span>
                                                </Link>
                                            ) : (
                                                '—'
                                            )}
                                        </Td>
                                        <Td>
                                            <Chip tone={alliance.recruiting ? 'success' : 'neutral'}>
                                                {alliance.recruiting ? 'Open' : 'Closed'}
                                            </Chip>
                                        </Td>
                                        <Td align="right">
                                            {myAllianceId ? (
                                                isMine ? <Chip tone="success">Your alliance</Chip> : null
                                            ) : hasApplied ? (
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    size="sm"
                                                    loading={busy}
                                                    onClick={() => onCancelApplication(alliance.id)}
                                                >
                                                    Cancel application
                                                </Button>
                                            ) : (
                                                <div className="flex flex-col items-end gap-1">
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        size="sm"
                                                        disabled={!alliance.recruiting}
                                                        loading={busy}
                                                        onClick={() => onApply(alliance)}
                                                    >
                                                        Apply
                                                    </Button>
                                                    {/* Native `title` never shows on a disabled button — say it in text. */}
                                                    {!alliance.recruiting && (
                                                        <span className="text-micro text-content-muted">Not recruiting</span>
                                                    )}
                                                </div>
                                            )}
                                        </Td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
